import { useState, useEffect } from 'react';
import { Layout } from '../components/Layout';
import { useLanguage } from '../context/LanguageContext';
import { productsAPI, priceHistoryAPI } from '../lib/api';
import { formatCurrency, formatNumber, getCategoryLabel, getStockStatus } from '../lib/utils';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import { toast } from 'sonner';
import { Plus, PencilSimple, Trash, Package, MagnifyingGlass, DownloadSimple, UploadSimple, ClockCounterClockwise } from '@phosphor-icons/react';
import { TrendingUp, TrendingDown } from 'lucide-react';
import api from '../lib/api';

const categories = [
  { value: 'et', label: 'Et' },
  { value: 'sebze', label: 'Sebze' },
  { value: 'baharat', label: 'Baharat' },
  { value: 'icecek', label: 'İçecek' },
  { value: 'alkollu_icecek', label: 'Alkollü İçecek' },
  { value: 'temizlik', label: 'Temizlik' },
  { value: 'paketleme', label: 'Paketleme' },
  { value: 'gida', label: 'Gıda' },
  { value: 'karkas', label: 'Karkas/Gövde' },
  { value: 'islenenmis', label: 'İşlenmiş Et' },
  { value: 'diger', label: 'Diğer' },
];

const units = [
  { value: 'kg', label: 'Kilogram (kg)' },
  { value: 'gr', label: 'Gram (gr)' },
  { value: 'lt', label: 'Litre (lt)' },
  { value: 'ml', label: 'Mililitre (ml)' },
  { value: 'adet', label: 'Adet' },
  { value: 'koli', label: 'Koli' },
  { value: 'paket', label: 'Paket' },
];

// Alış ve stok birimleri için ayrı listeler
const purchaseUnits = [
  { value: 'kg', label: 'Kilogram (KG)' },
  { value: 'gr', label: 'Gram' },
  { value: 'lt', label: 'Litre (LT)' },
  { value: 'ml', label: 'Mililitre (ML)' },
  { value: 'adet', label: 'Adet' },
  { value: 'koli', label: 'Koli' },
  { value: 'paket', label: 'Paket' },
];

const recipeUnits = [
  { value: 'kg', label: 'Kilogram (KG)' },
  { value: 'gr', label: 'Gram' },
  { value: 'lt', label: 'Litre (LT)' },
  { value: 'ml', label: 'Mililitre (ML)' },
  { value: 'adet', label: 'Adet' },
];

const stockUnits = [...recipeUnits, { value: 'koli', label: 'Koli (Depo) / Adet (Mağaza)' }];

export default function Products() {
  const { t, language } = useLanguage();
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [historyDialogOpen, setHistoryDialogOpen] = useState(false);
  const [editingProduct, setEditingProduct] = useState(null);
  const [deletingProduct, setDeletingProduct] = useState(null);
  const [selectedProductHistory, setSelectedProductHistory] = useState(null);
  const [priceHistory, setPriceHistory] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('all');
  
  const [formData, setFormData] = useState({
    name: '',
    category: 'et',
    unit: 'kg',
    unit_price: '',
    min_stock_level: '0',
    fire_rate: '0',
    units_per_package: '',
    package_price: '',
    // Birim Dönüşüm Alanları (Faz 1B)
    purchase_unit: '',
    stock_unit: '',
    recipe_unit: '',
    conversion_factor: '',
    conversion_sample_qty: '',
    conversion_sample_weight: '',
  });
  
  const [isUploading, setIsUploading] = useState(false);
  const fileInputRef = useState(null);

  useEffect(() => {
    fetchProducts();
  }, []);

  const fetchProducts = async () => {
    try {
      const res = await productsAPI.getAll();
      setProducts(res.data);
    } catch (error) {
      toast.error('Ürünler yüklenirken hata oluştu');
    } finally {
      setLoading(false);
    }
  };

  const handleExportExcel = () => {
    const backendUrl = process.env.REACT_APP_BACKEND_URL || '';
    const link = document.createElement('a');
    link.href = `${backendUrl}/api/products/export/excel`;
    link.download = `fiyat_listesi_${new Date().toISOString().split('T')[0]}.xlsx`;
    link.target = '_blank';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    toast.success('Liste indiriliyor...');
  };

  const handleImportExcel = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    
    if (!file.name.endsWith('.xlsx') && !file.name.endsWith('.xls')) {
      toast.error('Sadece Excel dosyaları (.xlsx, .xls) kabul edilir');
      return;
    }
    
    setIsUploading(true);
    
    try {
      const formData = new FormData();
      formData.append('file', file);
      
      const response = await api.post('/products/import/prices/upload', formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });
      
      toast.success(response.data.message);
      
      if (response.data.errors?.length > 0) {
        response.data.errors.forEach(err => toast.warning(err));
      }
      
      fetchProducts();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Fiyat güncelleme başarısız');
    } finally {
      setIsUploading(false);
      // Input'u temizle
      event.target.value = '';
    }
  };

  const handleDownloadTemplate = () => {
    const backendUrl = process.env.REACT_APP_BACKEND_URL || '';
    const link = document.createElement('a');
    link.href = `${backendUrl}/api/products/template/excel`;
    link.download = 'urun_sablonu.xlsx';
    link.target = '_blank';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    toast.success('Şablon indiriliyor...');
  };

  const handleImportProducts = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    
    if (!file.name.endsWith('.xlsx') && !file.name.endsWith('.xls')) {
      toast.error('Sadece Excel dosyaları (.xlsx, .xls) kabul edilir');
      return;
    }
    
    setIsUploading(true);
    
    try {
      const formData = new FormData();
      formData.append('file', file);
      
      const response = await api.post('/products/import/excel', formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });
      
      toast.success(response.data.message);
      
      if (response.data.errors?.length > 0) {
        response.data.errors.forEach(err => toast.warning(err));
      }
      
      fetchProducts();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Ürün yükleme başarısız');
    } finally {
      setIsUploading(false);
      event.target.value = '';
    }
  };

  const handleOpenDialog = (product = null) => {
    if (product) {
      setEditingProduct(product);
      setFormData({
        name: product.name,
        category: product.category,
        unit: product.unit,
        unit_price: product.unit_price.toString(),
        min_stock_level: product.min_stock_level.toString(),
        fire_rate: (product.fire_rate || 0).toString(),
        units_per_package: product.units_per_package ? product.units_per_package.toString() : '',
        package_price: product.package_price ? product.package_price.toString() : '',
        // Birim Dönüşüm Alanları (Faz 1B)
        purchase_unit: product.purchase_unit || '',
        stock_unit: product.stock_unit || '',
        recipe_unit: product.recipe_unit || '',
        conversion_factor: product.conversion_factor ? product.conversion_factor.toString() : '',
        conversion_sample_qty: product.conversion_sample_qty ? product.conversion_sample_qty.toString() : '',
        conversion_sample_weight: product.conversion_sample_weight ? product.conversion_sample_weight.toString() : '',
      });
    } else {
      setEditingProduct(null);
      setFormData({
        name: '',
        category: 'et',
        unit: 'kg',
        unit_price: '',
        min_stock_level: '0',
        fire_rate: '0',
        units_per_package: '',
        package_price: '',
        // Birim Dönüşüm Alanları (Faz 1B)
        purchase_unit: '',
        stock_unit: '',
        recipe_unit: '',
        conversion_factor: '',
        conversion_sample_qty: '',
        conversion_sample_weight: '',
      });
    }
    setDialogOpen(true);
  };

  const handleOpenHistory = async (product) => {
    setSelectedProductHistory(product);
    setHistoryDialogOpen(true);
    setHistoryLoading(true);
    
    try {
      const res = await priceHistoryAPI.getByProduct(product.id);
      setPriceHistory(res.data);
    } catch (error) {
      toast.error('Fiyat tarihçesi yüklenemedi');
    } finally {
      setHistoryLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Koli fiyatı girilmişse birim fiyatı hesapla
    let unitPrice = parseFloat(formData.unit_price) || 0;
    const unitsPerPackage = parseInt(formData.units_per_package) || null;
    const packagePrice = parseFloat(formData.package_price) || null;
    
    if (packagePrice && unitsPerPackage && unitsPerPackage > 0 && !formData.unit_price) {
      unitPrice = packagePrice / unitsPerPackage;
    }
    
    if (!formData.name || (!formData.unit_price && !packagePrice)) {
      toast.error('Lütfen ürün adı ve fiyat bilgisini girin');
      return;
    }

    if (formData.stock_unit === 'koli' && !(parseFloat(formData.conversion_factor) > 0)) {
      toast.error('Koli stok birimi için "1 koli = X adet" değerini girin');
      return;
    }

    const data = {
      name: formData.name,
      category: formData.category,
      unit: formData.unit,
      unit_price: unitPrice,
      min_stock_level: parseFloat(formData.min_stock_level) || 0,
      fire_rate: parseFloat(formData.fire_rate) || 0,
      units_per_package: unitsPerPackage,
      package_price: packagePrice,
      // Birim Dönüşüm Alanları (Faz 1B)
      purchase_unit: formData.purchase_unit || null,
      stock_unit: formData.stock_unit || null,
      recipe_unit: formData.recipe_unit || null,
      conversion_factor: parseFloat(formData.conversion_factor) || null,
      conversion_sample_qty: parseFloat(formData.conversion_sample_qty) || null,
      conversion_sample_weight: parseFloat(formData.conversion_sample_weight) || null,
    };

    try {
      if (editingProduct) {
        await productsAPI.update(editingProduct.id, data);
        toast.success('Ürün güncellendi');
      } else {
        await productsAPI.create(data);
        toast.success('Ürün eklendi');
      }
      setDialogOpen(false);
      fetchProducts();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'İşlem başarısız');
    }
  };

  const handleDelete = async () => {
    if (!deletingProduct) return;
    
    try {
      await productsAPI.delete(deletingProduct.id);
      toast.success('Ürün silindi');
      setDeleteDialogOpen(false);
      setDeletingProduct(null);
      fetchProducts();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Silme işlemi başarısız');
    }
  };

  const filteredProducts = products.filter((p) => {
    const matchesSearch = p.name.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesCategory = categoryFilter === 'all' || p.category === categoryFilter;
    return matchesSearch && matchesCategory;
  });

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="font-heading text-3xl lg:text-4xl font-bold tracking-tight">
              {t('products.title')}
            </h1>
            <p className="mt-2 text-muted-foreground">
              {t('products.subtitle')}
            </p>
          </div>
          <div className="flex gap-2 flex-wrap">
            {/* Şablon İndir */}
            <Button
              variant="outline"
              onClick={handleDownloadTemplate}
              data-testid="download-template-button"
              title="Boş ürün şablonu indir"
            >
              <DownloadSimple size={20} weight="bold" className="mr-2" />
              Şablon İndir
            </Button>
            
            {/* Toplu Ürün Yükle */}
            <div className="relative">
              <input
                type="file"
                accept=".xlsx,.xls"
                onChange={handleImportProducts}
                className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
                disabled={isUploading}
                data-testid="import-products-input"
              />
              <Button
                variant="outline"
                disabled={isUploading}
                data-testid="import-products-button"
                className="bg-green-50 border-green-200 hover:bg-green-100 text-green-700"
              >
                <UploadSimple size={20} weight="bold" className="mr-2" />
                {isUploading ? 'Yükleniyor...' : 'Toplu Ürün Yükle'}
              </Button>
            </div>
            
            {/* Mevcut Fiyat Listesi */}
            <Button
              variant="outline"
              onClick={handleExportExcel}
              data-testid="export-excel-button"
              title="Mevcut ürün listesini indir"
            >
              <DownloadSimple size={20} weight="bold" className="mr-2" />
              Liste İndir
            </Button>
            
            {/* Fiyat Güncelle */}
            <div className="relative">
              <input
                type="file"
                accept=".xlsx,.xls"
                onChange={handleImportExcel}
                className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
                disabled={isUploading}
                data-testid="import-excel-input"
              />
              <Button
                variant="outline"
                disabled={isUploading}
                data-testid="import-excel-button"
              >
                <UploadSimple size={20} weight="bold" className="mr-2" />
                Fiyat Güncelle
              </Button>
            </div>
            
            {/* Yeni Ürün */}
            <Button
              onClick={() => handleOpenDialog()}
              className="bg-primary hover:bg-primary/90"
              data-testid="add-product-button"
            >
              <Plus size={20} weight="bold" className="mr-2" />
              Yeni Ürün
            </Button>
          </div>
        </div>

        {/* Filters */}
        <div className="flex flex-col sm:flex-row gap-4">
          <div className="relative flex-1">
            <MagnifyingGlass size={20} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <Input
              placeholder="Ürün ara..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10 h-11"
              data-testid="search-products"
            />
          </div>
          <Select value={categoryFilter} onValueChange={setCategoryFilter}>
            <SelectTrigger className="w-full sm:w-48 h-11" data-testid="category-filter">
              <SelectValue placeholder="Kategori" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Tüm Kategoriler</SelectItem>
              {categories.map((cat) => (
                <SelectItem key={cat.value} value={cat.value}>
                  {cat.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {/* Products Table */}
        <div className="border border-border bg-card rounded-xl overflow-hidden">
          <Table className="data-table">
            <TableHeader>
              <TableRow className="border-b border-border bg-muted/30">
                <TableHead className="py-4">Ürün Adı</TableHead>
                <TableHead>Kategori</TableHead>
                <TableHead>Birim</TableHead>
                <TableHead className="text-right">Birim Fiyat</TableHead>
                <TableHead className="text-right">Depo</TableHead>
                <TableHead className="text-right">Mağaza</TableHead>
                <TableHead className="text-center">Durum</TableHead>
                <TableHead className="text-right">İşlemler</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredProducts.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={8} className="text-center py-12">
                    <Package size={48} weight="duotone" className="mx-auto text-muted-foreground/50 mb-4" />
                    <p className="text-muted-foreground">Henüz ürün bulunmuyor</p>
                  </TableCell>
                </TableRow>
              ) : (
                filteredProducts.map((product) => {
                  const totalStock = product.warehouse_stock + product.store_stock;
                  const status = getStockStatus(totalStock, product.min_stock_level);
                  
                  return (
                    <TableRow 
                      key={product.id} 
                      className="table-row-hover border-b border-border/40"
                      data-testid={`product-row-${product.id}`}
                    >
                      <TableCell className="col-name font-medium py-4">{product.name}</TableCell>
                      <TableCell>
                        <span className="badge-pill bg-muted text-muted-foreground">
                          {getCategoryLabel(product.category)}
                        </span>
                      </TableCell>
                      <TableCell className="text-muted-foreground">{product.unit}</TableCell>
                      <TableCell className="text-right col-number">
                        {formatCurrency(product.unit_price)}
                      </TableCell>
                      <TableCell className="text-right col-number">
                        {formatNumber(product.warehouse_stock)}
                        {product.stock_unit === 'koli' && <span className="ml-1 text-xs text-muted-foreground">koli</span>}
                      </TableCell>
                      <TableCell className="text-right col-number">
                        {formatNumber(product.store_stock)}
                      </TableCell>
                      <TableCell className="text-center">
                        <span className={`badge-pill status-${status}`}>
                          {status === 'critical' ? 'Kritik' : status === 'low' ? 'Düşük' : 'Normal'}
                        </span>
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex items-center justify-end gap-1">
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => handleOpenHistory(product)}
                            title="Fiyat Tarihçesi"
                            data-testid={`history-product-${product.id}`}
                          >
                            <ClockCounterClockwise size={18} className="text-muted-foreground" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => handleOpenDialog(product)}
                            data-testid={`edit-product-${product.id}`}
                          >
                            <PencilSimple size={18} />
                          </Button>
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => {
                              setDeletingProduct(product);
                              setDeleteDialogOpen(true);
                            }}
                            className="text-destructive hover:text-destructive"
                            data-testid={`delete-product-${product.id}`}
                          >
                            <Trash size={18} />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </div>

        {/* Add/Edit Dialog */}
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogContent className="sm:max-w-md">
            <DialogHeader>
              <DialogTitle className="font-heading text-xl">
                {editingProduct ? 'Ürün Düzenle' : 'Yeni Ürün Ekle'}
              </DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="space-y-2">
                <Label className="text-xs uppercase tracking-widest text-muted-foreground">
                  Ürün Adı
                </Label>
                <Input
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  placeholder="Domates"
                  className="h-11"
                  data-testid="product-name-input"
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label className="text-xs uppercase tracking-widest text-muted-foreground">
                    Kategori
                  </Label>
                  <Select
                    value={formData.category}
                    onValueChange={(value) => setFormData({ ...formData, category: value })}
                  >
                    <SelectTrigger className="h-11" data-testid="product-category-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {categories.map((cat) => (
                        <SelectItem key={cat.value} value={cat.value}>
                          {cat.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label className="text-xs uppercase tracking-widest text-muted-foreground">
                    Birim
                  </Label>
                  <Select
                    value={formData.unit}
                    onValueChange={(value) => setFormData({ ...formData, unit: value })}
                  >
                    <SelectTrigger className="h-11" data-testid="product-unit-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {units.map((unit) => (
                        <SelectItem key={unit.value} value={unit.value}>
                          {unit.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label className="text-xs uppercase tracking-widest text-muted-foreground">
                    Birim Fiyat (TL)
                  </Label>
                  <Input
                    type="number"
                    step="0.01"
                    value={formData.unit_price}
                    onChange={(e) => setFormData({ ...formData, unit_price: e.target.value })}
                    placeholder="0.00"
                    className="h-11"
                    data-testid="product-price-input"
                  />
                </div>

                <div className="space-y-2">
                  <Label className="text-xs uppercase tracking-widest text-muted-foreground">
                    Min. Stok Seviyesi
                  </Label>
                  <Input
                    type="number"
                    step="0.01"
                    value={formData.min_stock_level}
                    onChange={(e) => setFormData({ ...formData, min_stock_level: e.target.value })}
                    placeholder="0"
                    className="h-11"
                    data-testid="product-min-stock-input"
                  />
                </div>
              </div>
              
              {/* Koli Bilgileri */}
              <div className="border border-dashed border-border rounded-lg p-4 space-y-4">
                <p className="text-sm font-medium text-muted-foreground">Koli/Paket Bilgileri (Opsiyonel)</p>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label className="text-xs uppercase tracking-widest text-muted-foreground">
                      Koli İçi Adet
                    </Label>
                    <Input
                      type="number"
                      value={formData.units_per_package}
                      onChange={(e) => {
                        const units = parseInt(e.target.value) || '';
                        setFormData({ ...formData, units_per_package: e.target.value });
                        // Koli fiyatı varsa birim fiyatı otomatik hesapla
                        if (units && formData.package_price) {
                          const unitPrice = parseFloat(formData.package_price) / units;
                          setFormData(prev => ({ ...prev, units_per_package: e.target.value, unit_price: unitPrice.toFixed(2) }));
                        }
                      }}
                      placeholder="Örn: 24"
                      className="h-11"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-xs uppercase tracking-widest text-muted-foreground">
                      Koli Fiyatı (TL)
                    </Label>
                    <Input
                      type="number"
                      step="0.01"
                      value={formData.package_price}
                      onChange={(e) => {
                        const pkgPrice = parseFloat(e.target.value) || '';
                        setFormData({ ...formData, package_price: e.target.value });
                        // Koli içi adet varsa birim fiyatı otomatik hesapla
                        if (pkgPrice && formData.units_per_package) {
                          const unitPrice = pkgPrice / parseInt(formData.units_per_package);
                          setFormData(prev => ({ ...prev, package_price: e.target.value, unit_price: unitPrice.toFixed(2) }));
                        }
                      }}
                      placeholder="0.00"
                      className="h-11"
                    />
                  </div>
                </div>
                {formData.units_per_package && formData.package_price && (
                  <p className="text-xs text-muted-foreground">
                    Hesaplanan Birim Fiyat: ₺{(parseFloat(formData.package_price) / parseInt(formData.units_per_package)).toFixed(2)}
                  </p>
                )}
              </div>

              {/* Birim Dönüşüm Bölümü (Faz 1B) */}
              <div className="border-t pt-4 mt-4">
                <p className="text-sm font-medium mb-3 text-primary">Birim Dönüşüm Ayarları (Opsiyonel)</p>
                <p className="text-xs text-muted-foreground mb-4">
                  KG ile alıp ADET olarak tüketen ürünler için dönüşüm bilgisi girin.
                </p>
                
                <div className="grid grid-cols-3 gap-4 mb-4">
                  <div className="space-y-2">
                    <Label className="text-xs uppercase tracking-widest text-muted-foreground">
                      Alış Birimi
                    </Label>
                    <Select
                      value={formData.purchase_unit || formData.unit}
                      onValueChange={(value) => setFormData({ ...formData, purchase_unit: value })}
                    >
                      <SelectTrigger className="h-11">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {purchaseUnits.map((unit) => (
                          <SelectItem key={unit.value} value={unit.value}>
                            {unit.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label className="text-xs uppercase tracking-widest text-muted-foreground">
                      Stok Birimi
                    </Label>
                    <Select
                      value={formData.stock_unit || formData.unit}
                      onValueChange={(value) => setFormData({
                        ...formData,
                        stock_unit: value,
                        recipe_unit: value === 'koli' ? (formData.recipe_unit || formData.unit) : value,
                        conversion_factor: value === 'koli' ? (formData.conversion_factor || formData.units_per_package || '') : formData.conversion_factor,
                      })}
                    >
                      <SelectTrigger className="h-11" data-testid="product-stock-unit-select">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {stockUnits.map((unit) => (
                          <SelectItem key={unit.value} value={unit.value}>
                            {unit.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label className="text-xs uppercase tracking-widest text-muted-foreground">
                      Reçete Birimi
                    </Label>
                    <Select
                      value={formData.recipe_unit || formData.stock_unit || formData.unit}
                      onValueChange={(value) => setFormData({ ...formData, recipe_unit: value })}
                    >
                      <SelectTrigger className="h-11">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {recipeUnits.map((unit) => (
                          <SelectItem key={unit.value} value={unit.value}>
                            {unit.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                {formData.stock_unit === 'koli' && (
                  <div className="bg-muted/50 p-4 rounded-lg space-y-2">
                    <Label className="text-xs">1 koli = X adet</Label>
                    <Input
                      type="number"
                      step="1"
                      value={formData.conversion_factor}
                      onChange={(e) => setFormData({ ...formData, conversion_factor: e.target.value })}
                      placeholder="Örn: 24"
                      className="h-10"
                      data-testid="product-koli-factor-input"
                    />
                    <p className="text-xs text-muted-foreground">
                      Depo stoğu koli, mağaza stoğu adet olarak tutulur. Birim fiyat adet fiyatıdır. Mevcut stok sayıları değişmez.
                    </p>
                  </div>
                )}

                {/* Dönüşüm Katsayısı Hesaplama */}
                {formData.stock_unit !== 'koli' && ((formData.purchase_unit || formData.unit) !== (formData.stock_unit || formData.unit)) && (
                  <div className="bg-muted/50 p-4 rounded-lg space-y-4">
                    <p className="text-sm font-medium">Dönüşüm Katsayısı Hesaplama</p>
                    <div className="grid grid-cols-3 gap-4">
                      <div className="space-y-2">
                        <Label className="text-xs">Örnek Miktar ({formData.stock_unit || 'adet'})</Label>
                        <Input
                          type="number"
                          step="1"
                          value={formData.conversion_sample_qty}
                          onChange={(e) => {
                            const sampleQty = parseFloat(e.target.value) || 0;
                            const sampleWeight = parseFloat(formData.conversion_sample_weight) || 0;
                            const factor = sampleQty > 0 && sampleWeight > 0 ? sampleWeight / sampleQty : 0;
                            setFormData({
                              ...formData,
                              conversion_sample_qty: e.target.value,
                              conversion_factor: factor > 0 ? factor.toFixed(2) : ''
                            });
                          }}
                          placeholder="Örn: 10"
                          className="h-10"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label className="text-xs">Toplam Ağırlık (gram)</Label>
                        <Input
                          type="number"
                          step="0.01"
                          value={formData.conversion_sample_weight}
                          onChange={(e) => {
                            const sampleWeight = parseFloat(e.target.value) || 0;
                            const sampleQty = parseFloat(formData.conversion_sample_qty) || 0;
                            const factor = sampleQty > 0 && sampleWeight > 0 ? sampleWeight / sampleQty : 0;
                            setFormData({
                              ...formData,
                              conversion_sample_weight: e.target.value,
                              conversion_factor: factor > 0 ? factor.toFixed(2) : ''
                            });
                          }}
                          placeholder="Örn: 242"
                          className="h-10"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label className="text-xs">1 {formData.stock_unit || 'adet'} = X gram</Label>
                        <Input
                          type="number"
                          step="0.01"
                          value={formData.conversion_factor}
                          onChange={(e) => setFormData({ ...formData, conversion_factor: e.target.value })}
                          placeholder="Otomatik hesaplanır"
                          className="h-10 bg-primary/5"
                        />
                      </div>
                    </div>

                    {/* Hesaplama Önizleme */}
                    {formData.conversion_factor && formData.unit_price && (
                      <div className="mt-4 p-3 bg-green-50 dark:bg-green-950/30 rounded border border-green-200 dark:border-green-800">
                        <p className="text-sm font-medium text-green-700 dark:text-green-400 mb-2">Hesaplama Önizleme</p>
                        <div className="grid grid-cols-2 gap-4 text-sm">
                          <div>
                            <span className="text-muted-foreground">1 {formData.stock_unit || 'adet'} ağırlığı:</span>
                            <span className="font-medium ml-2">{formData.conversion_factor} gram</span>
                          </div>
                          <div>
                            <span className="text-muted-foreground">1 {formData.stock_unit || 'adet'} maliyeti:</span>
                            <span className="font-medium ml-2">
                              ₺{(parseFloat(formData.unit_price) * (parseFloat(formData.conversion_factor) / 1000)).toFixed(4)}
                            </span>
                          </div>
                          {formData.unit_price && (
                            <>
                              <div>
                                <span className="text-muted-foreground">Örnek: 1 KG alışta:</span>
                                <span className="font-medium ml-2">
                                  {Math.floor(1000 / parseFloat(formData.conversion_factor))} adet
                                </span>
                              </div>
                              <div>
                                <span className="text-muted-foreground">Toplam 1 KG değeri:</span>
                                <span className="font-medium ml-2">
                                  ₺{parseFloat(formData.unit_price).toFixed(2)}
                                </span>
                              </div>
                            </>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>

              <DialogFooter>
                <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>
                  İptal
                </Button>
                <Button type="submit" className="bg-primary hover:bg-primary/90" data-testid="product-submit">
                  {editingProduct ? 'Güncelle' : 'Ekle'}
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>

        {/* Delete Confirmation Dialog */}
        <Dialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
          <DialogContent className="sm:max-w-md">
            <DialogHeader>
              <DialogTitle className="font-heading text-xl">Ürünü Sil</DialogTitle>
            </DialogHeader>
            <p className="text-muted-foreground">
              <strong>{deletingProduct?.name}</strong> ürününü silmek istediğinizden emin misiniz? Bu işlem geri alınamaz.
            </p>
            <DialogFooter>
              <Button variant="outline" onClick={() => setDeleteDialogOpen(false)}>
                İptal
              </Button>
              <Button
                variant="destructive"
                onClick={handleDelete}
                data-testid="confirm-delete-product"
              >
                Sil
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Price History Dialog */}
        <Dialog open={historyDialogOpen} onOpenChange={setHistoryDialogOpen}>
          <DialogContent className="sm:max-w-lg max-h-[80vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="font-heading text-xl flex items-center gap-2">
                <ClockCounterClockwise size={24} />
                Fiyat Tarihçesi
              </DialogTitle>
            </DialogHeader>
            
            {selectedProductHistory && (
              <div className="space-y-4">
                <div className="p-4 bg-muted/50 rounded-lg">
                  <p className="font-semibold text-lg">{selectedProductHistory.name}</p>
                  <p className="text-sm text-muted-foreground">
                    Mevcut Fiyat: <span className="font-data text-primary">{formatCurrency(selectedProductHistory.unit_price)}</span> / {selectedProductHistory.unit}
                  </p>
                </div>
                
                {historyLoading ? (
                  <div className="flex justify-center py-8">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
                  </div>
                ) : priceHistory.length === 0 ? (
                  <div className="text-center py-8 text-muted-foreground">
                    Bu ürün için fiyat değişikliği kaydı bulunmuyor
                  </div>
                ) : (
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Tarih</TableHead>
                        <TableHead className="text-right">Eski</TableHead>
                        <TableHead className="text-right">Yeni</TableHead>
                        <TableHead className="text-center">Değişim</TableHead>
                        <TableHead>Kullanıcı</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {priceHistory.map((record) => (
                        <TableRow key={record.id}>
                          <TableCell className="text-sm">
                            {new Date(record.changed_at).toLocaleDateString('tr-TR')}
                          </TableCell>
                          <TableCell className="text-right font-data text-muted-foreground">
                            {formatCurrency(record.old_price)}
                          </TableCell>
                          <TableCell className="text-right font-data">
                            {formatCurrency(record.new_price)}
                          </TableCell>
                          <TableCell className="text-center">
                            <Badge 
                              variant={record.change_percentage >= 0 ? "destructive" : "default"}
                              className={`${record.change_percentage >= 0 ? 'bg-red-500/10 text-red-500' : 'bg-green-500/10 text-green-500'} border-0`}
                            >
                              <span className="flex items-center gap-1">
                                {record.change_percentage >= 0 ? (
                                  <TrendingUp className="w-3 h-3" />
                                ) : (
                                  <TrendingDown className="w-3 h-3" />
                                )}
                                {record.change_percentage >= 0 ? '+' : ''}{record.change_percentage}%
                              </span>
                            </Badge>
                          </TableCell>
                          <TableCell className="text-sm text-muted-foreground">
                            {record.changed_by_name}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                )}
              </div>
            )}
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
}
